import type { ConceptWorksheetFilterBody } from '@mathflat/domain/MakeWorksheet'
import type { ProblemLevel, TaggedProblem } from '@mathflat/domain/Problem'
import type { Student } from '@mathflat/domain/Student'
import { computed, makeObservable, observable, toJS } from 'mobx'

import { getStoredIncludeMyDbProblem } from '@/service/includeMyDbProblemStorage'
import type {
  ArithmeticLevel,
  MakeWorksheetBy,
  MakeWorksheetEqualityLevel,
  PracticeTestOption,
  PreviousExclusionScope,
  ProblemFilterType,
} from '@/types/makeWorksheet'

import { DTO } from '../DTO'
import type { MakeWorksheetFilterOption } from './MakeWorksheetFilterOption'

// 단원유형별 필터 조건. 오답학습지 필터(단원별·기간별·학습지별)도 이 조건에서 서버가 받는 것만 골라 보낸다.
// 연산이 아니면 난이도 계열을 늘 채운다
export type ChapterFilterParams = ConceptWorksheetFilterBody & {
  arithmeticOnly?: false
  level: NonNullable<ConceptWorksheetFilterBody['level']>
  levelWeight: number[]
  problemFilterType: NonNullable<ConceptWorksheetFilterBody['problemFilterType']>
  practiceTest: NonNullable<ConceptWorksheetFilterBody['practiceTest']>
}
// 단원별·기간별 오답 필터는 연산을 받지 않는다
export type LeveledChapterFilterParams = Omit<ChapterFilterParams, 'arithmeticOnly'>
// 연산은 난이도 계열 대신 arithmeticLevel 을 보낸다
export type ArithmeticChapterFilterParams = ConceptWorksheetFilterBody & { arithmeticOnly: true }

export class MakeWorksheetByChapterOption extends DTO {
  filterId = '' // 필터 고유 아이디

  // 공통
  type: MakeWorksheetBy = 'CONCEPT' // TODO - fix 학습지만들기 1단계가 어떤건지 : 'CONCEPT' - 단원유형 PUBLIC_WORKBOOK - 시중교재 PRACTICE_TEST - 수능 모의고사
  conceptIdList: number[] = [] // 유형 선택 아이디리스트
  excludedTopicIds: number[] = [] // 제외할 주제(유형) id list
  excludedSubTopicIds: number[] = [] // 제외할 세부(유형) id list
  problemCount = 50 // 문제 수
  level: ProblemLevel = 3 // 문제 난이도
  levelWeight: [number, number, number, number, number] = [0, 0, 100, 0, 0]
  problemFilterType: ProblemFilterType = 'ALL'
  practiceTest: PracticeTestOption = 'INCLUDE' // 모의고사 문제 포함여부 - include: 문제 포함, only: 문제만, exclude: 문제 제외
  onlyAutoScorable = true // 자동채점 가능여부
  excludePrevious = false // 기존 출제문제 제외여부
  previousExclusionScope?: PreviousExclusionScope // 기존 출제 문제 제외 기준
  studentIds?: Student['id'][] // 제외 기준이 STUDENT_ASSIGNED일 때 대상 학생 id 목록
  excludeOOC = true // 교육과정 외 유형 제외
  includeMyDbProblem = getStoredIncludeMyDbProblem() // 본인 업로드(나의 DB) 문제 포함 여부 — 최종 생성 시점 값 복원
  arithmeticOnly = false // 연산 문항만으로 구성 여부
  arithmeticLevel: ArithmeticLevel | null = null // 연산 난이도 3택 — level·levelWeight 를 대체
  equalityLevel: MakeWorksheetEqualityLevel = null // 균등배분시 기준 - 선택 안하면 null
  minRate = 0 // 정답률 - 최대
  maxRate = 100 // 정답률 - 최대
  problemList:
    | {
        id: number
        tagTop: TaggedProblem['tagTop']
        baseWorkbookProblemId?: number | null
      }[]
    | null = null
  title?: string
  selectedConceptIdList: number[] = [] // 선택한 유형별 개념
  selectedLittleChapterIdList: number[] = [] // 선택한 소단원별 개념
  source?: MakeWorksheetFilterOption['source']

  constructor(data?: Partial<MakeWorksheetByChapterOption>) {
    super()

    if (data) {
      Object.assign(this, data)
    }

    makeObservable(this, {
      filterId: observable,
      practiceTest: observable,
      onlyAutoScorable: observable,
      excludePrevious: observable,
      previousExclusionScope: observable,
      studentIds: observable,
      problemFilterType: observable,
      excludeOOC: observable,
      includeMyDbProblem: observable,
      type: observable,

      conceptIdList: observable,
      excludedTopicIds: observable,
      excludedSubTopicIds: observable,
      problemCount: observable,
      level: observable,
      levelWeight: observable,
      arithmeticOnly: observable,
      arithmeticLevel: observable,
      equalityLevel: observable,
      minRate: observable,
      maxRate: observable,
      problemList: observable,
      title: observable,
      selectedConceptIdList: observable,
      selectedLittleChapterIdList: observable,
      params: computed,
    })
  }

  get params(): ChapterFilterParams {
    const params: ChapterFilterParams = {
      practiceTest: this.practiceTest,
      onlyAutoScorable: this.onlyAutoScorable,
      excludePrevious: this.excludePrevious,
      problemFilterType: this.problemFilterType,
      conceptIdList: toJS(this.conceptIdList),
      excludedTopicIds: this.excludedTopicIds,
      excludedSubTopicIds: this.excludedSubTopicIds,
      problemCount: this.problemCount,
      level: this.level,
      levelWeight: this.levelWeight,
      excludeOOC: this.excludeOOC,
      includeMyDbProblem: this.includeMyDbProblem,
      equalityLevel: this.equalityLevel,
      minRate: this.minRate,
      maxRate: this.maxRate,
      problemList: toJS(this.problemList) || null,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
      source: this.source,
    }

    if (this.title) {
      params.title = this.title
    }
    if (this.filterId) {
      params.filterId = this.filterId
    }
    if (this.excludePrevious && this.previousExclusionScope) {
      params.previousExclusionScope = this.previousExclusionScope
      if (this.previousExclusionScope === 'STUDENT_ASSIGNED' && this.studentIds?.length) {
        params.studentIds = this.studentIds
      }
    }

    return params
  }

  // 연산은 level·levelWeight·problemFilterType·includeMyDbProblem 을 보내지 않는다 (서버가 채움)
  /** 오답 파생 필터 생성 요청 파라미터 — 연산이면 shape 자체가 갈린다 */
  get filterParams(): ChapterFilterParams | ArithmeticChapterFilterParams {
    return this.arithmeticOnly ? this.arithmeticParams : this.params
  }

  private get arithmeticParams(): ArithmeticChapterFilterParams {
    const params: ArithmeticChapterFilterParams = {
      arithmeticOnly: true,
      // 되읽기 null 대비 폴백 — 서버 기본값과 동일한 '전체'
      arithmeticLevel: this.arithmeticLevel ?? 'ALL',
      practiceTest: this.practiceTest,
      onlyAutoScorable: this.onlyAutoScorable,
      excludePrevious: this.excludePrevious,
      conceptIdList: toJS(this.conceptIdList),
      problemCount: this.problemCount,
      excludeOOC: this.excludeOOC,
      equalityLevel: this.equalityLevel,
      minRate: this.minRate,
      maxRate: this.maxRate,
      problemList: toJS(this.problemList) || null,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
      source: this.source,
    }

    if (this.title) {
      params.title = this.title
    }
    if (this.filterId) {
      params.filterId = this.filterId
    }
    if (this.excludePrevious && this.previousExclusionScope) {
      params.previousExclusionScope = this.previousExclusionScope
      if (this.previousExclusionScope === 'STUDENT_ASSIGNED' && this.studentIds?.length) {
        params.studentIds = this.studentIds
      }
    }

    return params
  }
}
